<script setup lang="ts">

import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import {ref, useTemplateRef, watch} from "vue";
import {QSelect} from "quasar";
import SuggestionProviderFieldWidgetUtil from "@/utils/editView/widgets/SuggestionProviderFieldWidgetUtil.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import {focusAndSelectInput, type NamaNavigateToNextEmit} from "@/utils/common/NamaVueWidget.ts";
import {useNamaBreakpoints} from "@/composables/useNamaBreakpoints.ts";

const props = defineProps<WidgetProps>();
const fieldId = props.field.id!;
const fieldUtil = new SuggestionProviderFieldWidgetUtil(props.presenter, props.fieldMetaData);
const options = ref<namaTypes.SuggestionProvidedItem[]>([]);
const shouldFilter = ref(false);
const loading = ref(false)
const popupVisible = ref(false);
const input = useTemplateRef<QSelect>("input");
const text = fieldUtil.formattedValue();
const emit = defineEmits<NamaNavigateToNextEmit>()
function onSuggest(val, update) {
  update(async () => {
      if (shouldFilter.value) {
        loading.value = true;
        await fieldUtil.suggest(val, options)
        loading.value = false;
      } else {
        options.value = [];
      }
    },
    newRef => {
      if (val !== '' && options.value.length > 0) {
        setTimeout(() => {
          newRef.setOptionIndex(-1)
          newRef.moveOptionSelection(1, true)
        });
      }
    })
}

function initFilter() {
  shouldFilter.value = true;
  if (!popupVisible.value) {
    input.value?.updateInputValue(fieldUtil.inputFieldValueRef.value || '', false);
    input.value?.showPopup();
  }
}

async function onDownHandler() {
  initFilter();
}

function onBlur() {
  fieldUtil.onBlur();
  shouldFilter.value = false;
}

function onEnterKeyDown(e: KeyboardEvent) {
  if (popupVisible.value)
    return;
  emit("navigateToNext", {keyboardEvent: e});
}

function focus() {
  focusAndSelectInput(input);
}

function canReceiveFocus() {
  return !fieldUtil.isFieldDisabled(props.forceEnableField);
}

defineExpose({focus, canReceiveFocus})

watch([fieldUtil.isFieldFocused, useNamaBreakpoints().isMobile], ([isFocused, isMobileValue]) => {
  if (!isFocused || !isMobileValue)
    return;
  initFilter()
})
</script>

<template>
  <q-select @keydown.enter="onEnterKeyDown"
    outlined use-input hide-selected fill-input dense hide-bottom-space hide-dropdown-icon options-dense
    :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
    @filter="onSuggest"
    @keydown.down="onDownHandler"
    @dblclick="onDownHandler"
    @popup-hide="popupVisible = false"
    @popup-show="popupVisible = true"
    input-debounce="0"
    :model-value="text"
    :loading="loading"
    :options="options"
    option-label="optionText"
    :type="showAsPassword ? 'password' : 'text'"
    ref="input"
    @input-value="fieldUtil.onPartialUserInput"
    @blur="onBlur"
    @focus="fieldUtil.onFocus"
    @update:model-value="fieldUtil.onSelect"
    :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
    style="contain: inline-size"
    class="nama-customize-dense-input tw-w-full"
    :class="{'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField)}"
    input-class="nama-input-class"
    options-selected-class="tw-pl-[6px] tw-pr-[3px] tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-text-primary-dark tw-text-white"
    popup-content-class="!tw-max-h-[30vh] tw-font-normal dark:tw-text-white dark:tw-bg-namaBackground-bright-dark">
    <template v-slot:option="scope">
      <q-item v-bind="scope.itemProps">
        <q-item-section>
          <q-item-label>{{ scope.opt.optionDisplayCode }}</q-item-label>
          <q-item-label caption class="dark:tw-text-[rgb(255,255,255,0.5)]"
                        v-if="scope.opt.optionCaption">
            {{ scope.opt.optionCaption }}
          </q-item-label>
        </q-item-section>
      </q-item>
    </template>
    <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
  </q-select>
</template>

<style scoped>

</style>
